<template>
  <transition
    name="msgbox-fade"
    @after-leave="afterLeave"
  >
    <div
      class="zm-message-box__wrapper"
      tabindex="-1"
      v-show="visible"
      @click.self="handleWrapperClick"
      role="dialog"
      aria-modal="true"
      :aria-labelledby="dialogTitleId"
      :aria-describedby="dialogContentId"
    >
      <div class="zm-message-box" :class="[customClass, center && 'zm-message-box--center', showStatusIcon && 'zm-message-box--show-status']">
        <div
          v-if="showStatusIcon"
          :class="['zm-message-box__status', icon]"
        >
        </div>
        <div class="zm-message-box__header" v-if="title !== null">
          <div class="zm-message-box__title">
            <div
              :class="['zm-message-box__status', icon]"
              v-if="icon && center"
            >
            </div>
            <span :id="dialogTitleId">{{ title }}</span>
          </div>
          <button
            type="button"
            class="zm-message-box__headerbtn"
            aria-label="Close"
            v-if="showClose"
            @click="handleAction(distinguishCancelAndClose ? 'close' : 'cancel')"
            @keydown.enter="handleAction(distinguishCancelAndClose ? 'close' : 'cancel')"
          >
            <i class="zm-message-box__close zm-icon-close"></i>
          </button>
        </div>
        <div class="zm-message-box__content">
          <div class="zm-message-box__message" v-if="message !== ''" :id="dialogContentId">
            <slot>
              <p v-if="!dangerouslyUseHTMLString">
                {{ message }}
              </p>
              <!-- eslint-disable vue/no-v-html -->
              <div v-else v-html="message"></div>
              <!-- eslint-enable -->
            </slot>
          </div>
          <div class="zm-message-box__input" v-show="showInput">
            <zm-input
              v-model="inputValue"
              :type="inputType"
              :label-by="dialogTitleId"
              @keydown.enter.native="handleInputEnter"
              :placeholder="inputPlaceholder"
              ref="input"
            />
            <div class="zm-message-box__errormsg" role="alert" :style="{ visibility: !!editorErrorMessage ? 'visible' : 'hidden' }">
              {{ editorErrorMessage }}
            </div>
          </div>
        </div>
        <div class="zm-message-box__btns">
          <zm-button
            :loading="confirmButtonLoading"
            ref="confirm"
            type="primary"
            :class="[ confirmButtonClasses ]"
            :disabled="confirmButtonDisabled"
            v-show="showConfirmButton"
            :round="roundButton"
            @click.native="handleAction('confirm')"
            @keydown.enter="handleAction('confirm')"
          >
            {{ confirmButtonText || t('messagebox.confirm') }}
          </zm-button>
          <zm-button
            :loading="cancelButtonLoading"
            :class="[ cancelButtonClasses ]"
            v-if="showCancelButton"
            :round="roundButton"
            @click.native="handleAction('cancel')"
            @keydown.enter="handleAction('cancel')"
          >
            {{ cancelButtonText || t('messagebox.cancel') }}
          </zm-button>
        </div>
      </div>
    </div>
  </transition>
</template>

<script type="text/babel">
  import '../../../i18n/messagebox';
  import Popup from '../../utils/popup';
  import Locale from '../../mixins/locale';
  import ZmInput from '../input';
  import ZmButton from '../button';
  import { addClass, removeClass } from '../../utils/dom';
  import { t } from '../../locale';
  import Dialog from '../../utils/aria-dialog';
  import { generateId } from '../../utils/util';

  const messageBoxId = generateId();
  let messageBox;
  let typeMap = {
    success: 'success',
    info: 'Megaphone',
    warning: 'warning',
    error: 'error'
  };

  export default {
    mixins: [Popup, Locale],

    props: {
      modal: {
        type: Boolean,
        default: true
      },
      lockScroll: {
        type: Boolean,
        default: true
      },
      showClose: {
        type: Boolean,
        default: false
      },
      closeOnClickModal: {
        type: Boolean,
        default: true
      },
      closeOnPressEscape: {
        type: Boolean,
        default: true
      },
      closeOnHashChange: {
        type: Boolean,
        default: true
      },
      center: {
        default: false,
        type: Boolean
      },
      roundButton: {
        default: false,
        type: Boolean
      }
    },

    components: {
      ZmInput,
      ZmButton
    },

    computed: {
      icon() {
        const {type, iconClass} = this;
        return iconClass || (type && typeMap[type] ? `zm-icon-${ typeMap[type] }` : '');
      },

      confirmButtonClasses() {
        return `${ this.confirmButtonClass }`;
      },
      cancelButtonClasses() {
        return `${ this.cancelButtonClass }`;
      },
      showStatusIcon() {
        return this.icon && !this.center && this.message !== '';
      }
    },

    methods: {
      getSafeClose() {
        const currentId = this.uid;
        return () => {
          this.$nextTick(() => {
            if (currentId === this.uid) this.doClose();
          });
        };
      },
      doClose() {
        if (!this.visible) return;
        this.visible = false;
        this._closing = true;
        this.confirmButtonDisabled = false;
        this.onClose && this.onClose();
        messageBox.closeDialog(); // 解绑
        if (this.lockScroll) {
          setTimeout(this.restoreBodyStyle, 200);
        }
        this.opened = false;
        this.doAfterClose();
        setTimeout(() => {
          if (this.action) this.callback(this.action, this);
        });
      },

      handleWrapperClick() {
        if (this.closeOnClickModal) {
          this.handleAction(this.distinguishCancelAndClose ? 'close' : 'cancel');
        }
      },

      handleInputEnter() {
        if (this.inputType !== 'textarea') {
          return this.handleAction('confirm');
        }
      },

      handleAction(action) {
        if (this.$type === 'prompt' && action === 'confirm' && !this.validate()) {
          return;
        }
        this.action = action;
        if (typeof this.beforeClose === 'function') {
          this.close = this.getSafeClose();
          this.beforeClose(action, this, this.close);
        } else {
          this.doClose();
        }
      },

      validate() {
        if (this.$type === 'prompt') {
          const inputPattern = this.inputPattern;
          if (inputPattern && !inputPattern.test(this.inputValue || '')) {
            this.editorErrorMessage = this.inputErrorMessage || t('messagebox.error');
            addClass(this.getInputElement(), 'invalid');
            return false;
          }
          const inputValidator = this.inputValidator;
          if (typeof inputValidator === 'function') {
            const validateResult = inputValidator(this.inputValue);
            if (validateResult === false) {
              this.editorErrorMessage = this.inputErrorMessage || t('messagebox.error');
              addClass(this.getInputElement(), 'invalid');
              return false;
            }
            if (typeof validateResult === 'string') {
              this.editorErrorMessage = validateResult;
              addClass(this.getInputElement(), 'invalid');
              return false;
            }
          }
        }
        this.editorErrorMessage = '';
        removeClass(this.getInputElement(), 'invalid');
        return true;
      },
      getFirstFocus() {
        const btn = this.$el.querySelector('.zm-message-box__btns .zm-button');
        const title = this.$el.querySelector('.zm-message-box__btns .zm-message-box__title');
        return btn || title;
      },
      getInputElement() {
        const inputRefs = this.$refs.input.$refs;
        return inputRefs.input || inputRefs.textarea;
      },
      afterLeave() {
        if (this.closed && typeof this.closed === 'function') {
          this.closed();
        }
      }
    },

    watch: {
      inputValue: {
        immediate: true,
        handler(val) {
          this.$nextTick(_ => {
            if (this.$type === 'prompt' && val !== null) {
              this.validate();
            }
          });
        }
      },

      visible(val) {
        if (val) {
          this.uid++;
          this.focusAfterClosed = document.activeElement;
          this.$nextTick(()=>{
            messageBox = new Dialog(this.$el, this.focusAfterClosed, this.getFirstFocus());
          });
        }

        // prompt
        if (this.$type !== 'prompt') return;
        if (val) {
          setTimeout(() => {
            if (this.$refs.input && this.$refs.input.$el) {
              this.getInputElement().focus();
            }
          }, 500);
        } else {
          this.editorErrorMessage = '';
          removeClass(this.getInputElement(), 'invalid');
        }
      }
    },

    mounted() {
      this.$nextTick(() => {
        if (this.closeOnHashChange) {
          window.addEventListener('hashchange', this.close);
        }
      });
      this.$on('updateConfirmButtonDisabled', (value) => {
        this.confirmButtonDisabled = value;
      });
    },

    beforeDestroy() {
      if (this.closeOnHashChange) {
        window.removeEventListener('hashchange', this.close);
      }
      setTimeout(() => {
        messageBox.closeDialog();
      });
    },

    data() {
      return {
        uid: 1,
        title: undefined,
        message: '',
        type: '',
        iconClass: '',
        customClass: '',
        showInput: false,
        inputValue: null,
        inputPlaceholder: '',
        inputType: 'text',
        inputPattern: null,
        inputValidator: null,
        inputErrorMessage: '',
        showConfirmButton: true,
        showCancelButton: false,
        action: '',
        confirmButtonText: '',
        cancelButtonText: '',
        confirmButtonLoading: false,
        cancelButtonLoading: false,
        confirmButtonClass: '',
        confirmButtonDisabled: false,
        cancelButtonClass: '',
        editorErrorMessage: null,
        callback: null,
        dangerouslyUseHTMLString: false,
        focusAfterClosed: null,
        isOnComposition: false,
        distinguishCancelAndClose: false,
        dialogTitleId: `message-box-title-${messageBoxId}`,
        dialogContentId: `message-box-content-${messageBoxId}`
      };
    }
  };
</script>

<style lang="scss">
@import "./style";
</style>
